import type { RoadPath } from './road-paths';
import type { SceneryChunk } from '@/modules/game-protocol/world-scenery';
import { indexedRoadClearance } from './road-clearance-index';
/**
 * Terrain and props around server-defined towns and roads.
 *
 * 1) Town centers + radii define settlement biome ground; saved layouts provide paving.
 * 2) A closed ring through towns (sorted by levelMin) defines the main road corridor.
 * 3) Optional `activeRoute` waypoints from `route_assigned` refine the path for the current leg.
 * 4) Wild tiles use the nearest town's biome as the base, then light noise.
 */

/** One town for terrain influence (from REST /towns or TownData). */
export interface TownTerrainInfluence {
  authored?: boolean;
  authoredPerimeter?: Array<{x:number;y:number}>;
  authoredBounds?: {minX:number;minY:number;maxX:number;maxY:number};
  id: number;
  cx: number;
  cy: number;
  radius: number;
  biome: string;
  levelMin: number;
}

/** Built once per frame batch / when towns or route change. */
export interface WorldTerrainContext {
  /** Presence, including [], selects server scenery exclusively; tools omit this property. */
  authoritativeScenery?: readonly SceneryChunk[];
  towns: TownTerrainInfluence[];
  /** Disable automatic modular scenery in authored previews/editor surfaces. Defaults to enabled in game rendering. */
  runtimeScenes?: boolean;
  /** Base biome for standalone wilderness fragments with no settlement influence. */
  wildernessBiome?: string;
  /** Pixel-space silhouettes occupied by authored fragment props; hides only intersecting procedural props. */
  sceneOcclusionBounds?: Array<{ left: number; top: number; right: number; bottom: number }>;
  /** Legacy ring fallback when roadNetwork is empty. */
  networkCenters: Array<{ x: number; y: number }>;
  /** All road polylines from the server graph (curved). */
  roadNetwork: Array<Array<{ x: number; y: number }>>;
  /** Explicit server-path classifications, retained through clipping. Omission means legacy main roads. */
  roadPaths?: readonly RoadPath[];
  /** Current route_assigned is always a main road; POI movement uses hero_move instead. */
  activeRoute: Array<{ x: number; y: number }> | null;
}

/** Deterministic integer hash for tile coordinates -> [0, 1) */
export function tileHash(x: number, y: number, seed: number): number {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + seed) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = h ^ (h >>> 16);
  return (h & 0x7fffffff) / 0x7fffffff;
}

function distPointToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const ab2 = abx * abx + aby * aby;
  let t = ab2 > 1e-12 ? (apx * abx + apy * aby) / ab2 : 0;
  t = Math.max(0, Math.min(1, t));
  const qx = ax + abx * t;
  const qy = ay + aby * t;
  return Math.hypot(px - qx, py - qy);
}

/** Open polyline: consecutive vertex pairs. */
export function minDistToOpenPolyline(
  px: number,
  py: number,
  pts: Array<{ x: number; y: number }>,
): number {
  if (pts.length < 2) return Number.POSITIVE_INFINITY;
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const d = distPointToSegment(px, py, a.x, a.y, b.x, b.y);
    if (d < min) min = d;
  }
  return min;
}

/** Closed ring through centers: (c0,c1)…(c_{n-2},c_{n-1}),(c_{n-1},c0). */
export function minDistToRing(
  px: number,
  py: number,
  centers: Array<{ x: number; y: number }>,
): number {
  const n = centers.length;
  if (n < 2) return Number.POSITIVE_INFINITY;
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < n; i++) {
    const a = centers[i]!;
    const b = centers[(i + 1) % n]!;
    const d = distPointToSegment(px, py, a.x, a.y, b.x, b.y);
    if (d < min) min = d;
  }
  return min;
}

/** Sort towns like the server chain (level_min, then id). */
export function orderedTownCenters(towns: TownTerrainInfluence[]): Array<{ x: number; y: number }> {
  const sorted = [...towns].sort((a, b) => a.levelMin - b.levelMin || a.id - b.id);
  return sorted.map((t) => ({ x: t.cx, y: t.cy }));
}

export function buildWorldTerrainContext(
  towns: TownTerrainInfluence[],
  activeRoute: Array<{ x: number; y: number }> | null,
  roadNetwork: Array<Array<{ x: number; y: number }>> = [],
): WorldTerrainContext {
  return {
    towns,
    networkCenters: orderedTownCenters(towns),
    roadNetwork,
    activeRoute: activeRoute && activeRoute.length >= 2 ? activeRoute : null,
  };
}

/** Half-length of the T-junction cross-arm at a roadside POI trigger (world units). */
export const ROAD_POI_CROSS_ARM_WU = 1.0;

export type RoadPOIGeometry = {
  biome: string;
  spineX?: number;
  spineY?: number;
  entryX?: number;
  entryY?: number;
  tangentX?: number;
  tangentY?: number;
  connectorWaypoints?: Array<{ x: number; y: number }>;
};

/**
 * Open polylines for POI spurs merged into roadNetwork so proceduralTerrain paints
 * the same `road` / `dirt` tiles as the main graph. Trigger/crossroads sit at spine on the parent road.
 */
export function roadPOIPaths(towns: RoadPOIGeometry[]): RoadPath[] {
  const out: RoadPath[] = [];
  for (const t of towns) {
    if (t.biome !== 'road') continue;
    const { spineX, spineY, entryX, entryY, tangentX, tangentY } = t;
    if (t.connectorWaypoints && t.connectorWaypoints.length >= 2) {
      out.push({points:t.connectorWaypoints.map((point) => ({ x: point.x, y: point.y })),kind:'poi-trail'});
    }
    if (
      spineX == null ||
      spineY == null ||
      entryX == null ||
      entryY == null ||
      tangentX == null ||
      tangentY == null
    ) {
      continue;
    }
    // Cross-arm at trigger/spine (on main road).
    out.push({kind:'main',points:[
      { x: spineX - tangentX * ROAD_POI_CROSS_ARM_WU, y: spineY - tangentY * ROAD_POI_CROSS_ARM_WU },
      { x: spineX + tangentX * ROAD_POI_CROSS_ARM_WU, y: spineY + tangentY * ROAD_POI_CROSS_ARM_WU },
    ]});
    // Spur from trigger into settlement (entry is between spine and plaza).
    if ((!t.connectorWaypoints || t.connectorWaypoints.length < 2)
      && Math.hypot(entryX - spineX, entryY - spineY) > 0.25) {
      out.push({kind:'poi-trail',points:[
        { x: spineX, y: spineY },
        { x: entryX, y: entryY },
      ]});
    }
  }
  return out;
}

export function roadPOISpurSegments(towns: RoadPOIGeometry[]): Array<Array<{ x: number; y: number }>> {
  return roadPOIPaths(towns).map(path=>path.points);
}

/** Main road graph plus roadside POI spur/cross-arm segments for terrain. */
export function mergeRoadNetworkWithPOISpurs(
  roadNetwork: Array<Array<{ x: number; y: number }>>,
  towns: RoadPOIGeometry[],
): Array<Array<{ x: number; y: number }>> {
  const spurs = roadPOISpurSegments(towns);
  if (spurs.length === 0) return roadNetwork;
  return [...roadNetwork, ...spurs];
}

/** Helpers for Minimap / tests: Town[] -> influences */
export function townsApiToInfluences(
  towns: Array<{
    id: number;
    worldX: number;
    worldY: number;
    radius: number;
    biome: string;
    levelMin: number;
  }>,
): TownTerrainInfluence[] {
  return towns.map((t) => ({
    id: t.id,
    cx: t.worldX,
    cy: t.worldY,
    radius: t.radius,
    biome: t.biome,
    levelMin: t.levelMin,
  }));
}

function roadClearance(wx: number, wy: number, ctx: WorldTerrainContext): number {
  const nearest = indexedRoadClearance(wx, wy, ctx.roadNetwork, ctx.activeRoute, ctx.roadPaths);
  if (!nearest.hasSegments && ctx.networkCenters.length >= 2) return minDistToRing(wx, wy, ctx.networkCenters);
  return nearest.distance;
}

function nearestTown(wx: number, wy: number, towns: TownTerrainInfluence[]): TownTerrainInfluence | null {
  if (towns.length === 0) return null;
  let best = towns[0]!;
  let bestD = Math.hypot(wx - best.cx, wy - best.cy);
  for (let i = 1; i < towns.length; i++) {
    const t = towns[i]!;
    const d = Math.hypot(wx - t.cx, wy - t.cy);
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}

/** Map server biome id to a ground tile key (colors in renderer). */
export function biomeToTerrainKey(biome: string): string {
  const b = biome.toLowerCase();
  if (b === 'forest') return 'forest_floor';
  if (b === 'ruins') return 'ruins_floor';
  if (b === 'canyon') return 'canyon_floor';
  if (b === 'swamp') return 'swamp_floor';
  if (b === 'volcanic') return 'volcanic_floor';
  if (b === 'astral') return 'astral_floor';
  if (b === 'winter') return 'winter_floor';
  if (b === 'jungle') return 'jungle_floor';
  if (b === 'meadow') return 'grass';
  return 'grass';
}

/**
 * Terrain at integer tile (wx, wy).
 * With `context`, roads follow the town ring + active route; wild tiles follow nearest town biome.
 * Without context, simple grass + noise (no synthetic diagonal road).
 */
export function proceduralTerrain(
  wx: number,
  wy: number,
  context?: WorldTerrainContext | null,
): string {
  const ctx = context;
  if (!ctx) {
    const h = tileHash(wx, wy, 42);
    if (h < 0.06) return 'dirt';
    if (h < 0.1) return 'stone';
    return 'grass';
  }
  if (ctx.towns.length === 0) {
    if (!ctx.wildernessBiome) {
      const h = tileHash(wx, wy, 42);
      if (h < 0.06) return 'dirt';
      if (h < 0.1) return 'stone';
      return 'grass';
    }
    const rd = roadClearance(wx, wy, ctx);
    if (rd < 1.15) return 'road';
    if (rd < 2.65) return 'dirt';
    const base = biomeToTerrainKey(ctx.wildernessBiome);
    const h = tileHash(wx, wy, 42);
    if (base === 'forest_floor' && h < 0.12) return 'dirt';
    if (base === 'swamp_floor' && h < 0.1) return 'dirt';
    if (h < 0.04) return 'stone';
    if (h < 0.09) return 'dirt';
    return base;
  }
  // Town paving belongs to saved street/plaza geometry, including while it loads.
  const town = ctx.towns.find(t => Math.hypot(wx - t.cx, wy - t.cy) <= t.radius);
  if (town) return biomeToTerrainKey(town.biome);
  const rd = roadClearance(wx, wy, ctx);
  if (rd < 1.15) return 'road';
  if (rd < 2.65) return 'dirt';

  const near = nearestTown(wx, wy, ctx.towns);
  const base = near ? biomeToTerrainKey(near.biome) : 'grass';
  const h = tileHash(wx, wy, 42);
  if (base === 'forest_floor' && h < 0.12) return 'dirt';
  if (base === 'swamp_floor' && h < 0.1) return 'dirt';
  if (h < 0.04) return 'stone';
  if (h < 0.09) return 'dirt';
  return base;
}

/**
 * Compute the distance from the nearest town edge (negative = inside town).
 * Returns Infinity if no towns.
 */
function townEdgeDist(wx: number, wy: number, towns: TownTerrainInfluence[]): number {
  let minEdge = Number.POSITIVE_INFINITY;
  for (const t of towns) {
    const d = Math.hypot(wx - t.cx, wy - t.cy) - t.radius;
    if (d < minEdge) minEdge = d;
  }
  return minEdge;
}

/**
 * Cluster-based noise: tiles near a "cluster seed" tile are more likely to share objects.
 * Returns a hash that correlates with nearby tiles (spatial coherence ~3 tiles).
 */
function clusterHash(wx: number, wy: number, seed: number): number {
  const cx = Math.floor(wx / 3);
  const cy = Math.floor(wy / 3);
  return tileHash(cx, cy, seed);
}

type CompositionTile = { object: string | null };

/** Full-range distance used only for rare roadside composition selection. */
function minDistanceToRoadNetwork(wx: number, wy: number, context: WorldTerrainContext): number {
  let min = Number.POSITIVE_INFINITY;
  for (const poly of context.roadNetwork) {
    min = Math.min(min, minDistToOpenPolyline(wx, wy, poly));
  }
  if (context.activeRoute) min = Math.min(min, minDistToOpenPolyline(wx, wy, context.activeRoute));
  return min;
}

/**
 * Deterministic, reusable prop groups. Each component remains a separate sprite,
 * while a macro-cell owns the layout so the wilderness reads as a scene.
 */
function compositionTile(
  wx: number,
  wy: number,
  terrain: string,
  roadDistance: number,
  context: WorldTerrainContext | null | undefined,
): CompositionTile | null {
  const cellX = Math.floor(wx / 12);
  const cellY = Math.floor(wy / 12);
  const localX = wx - cellX * 12;
  const localY = wy - cellY * 12;
  const seed = tileHash(cellX, cellY, 613);

  if (terrain === 'swamp_floor' && seed < 0.24) {
    if (localX >= 2 && localX <= 5 && localY >= 2 && localY <= 4) {
      let object: string | null = null;
      if ((localX === 2 || localX === 4) && localY === 2) object = 'marsh_reeds';
      if (localX === 3 && (localY === 2 || localY === 3)) object = 'swamp_stepping_stones';
      if (localX === 4 && localY === 3) object = 'bog_lights';
      if (localX === 5 && localY === 4) object = 'marsh_reeds';
      return { object: roadDistance >= 3 ? object : null };
    }
  }

  // A small roadside wreck: cart, fallen packhorse, crate and a hay bale.
  // Keep the whole footprint outside the clear travel corridor.
  if (
    (terrain === 'forest_floor' || terrain === 'grass' || terrain === 'dirt') &&
    seed < 0.035 &&
    localX >= 2 && localX <= 4 && localY >= 5 && localY <= 6
  ) {
    const wreckDistance = context
      ? minDistanceToRoadNetwork(cellX * 12 + 3, cellY * 12 + 5, context)
      : Number.POSITIVE_INFINITY;
    if (wreckDistance >= 3 && wreckDistance < 9) {
      let object: string | null = null;
      if (localX === 2 && localY === 5) object = 'crate';
      if (localX === 3 && localY === 5) object = 'cart';
      if (localX === 4 && localY === 5) object = 'fallen_horse';
      if (localX === 2 && localY === 6) object = 'hay_bale';
      return { object: roadDistance >= 3 ? object : null };
    }
  }

  // Meadow edges mix wildflowers; field remnants read as a small lived-in place.
  if (terrain === 'grass' && seed >= 0.035 && seed < 0.1) {
    if (localX >= 6 && localX <= 9 && localY >= 6 && localY <= 9) {
      let object: string | null = null;
      if (localX === 6 && localY === 6) object = 'field_scarecrow';
      if (localX === 7 && localY === 7) object = 'crop_patch';
      if (localX === 8 && localY === 8) object = 'harvest_sheaves';
      if (localX === 9 && localY === 9) object = 'meadow_wildflowers';
      return { object: roadDistance >= 3 ? object : null };
    }
  }

  // Ruins occupy a small footprint so broken masonry reads as one place.
  if (terrain === 'ruins_floor' && seed < 0.09) {
    if (localX >= 6 && localX <= 8 && localY >= 2 && localY <= 4) {
      let object: string | null = null;
      if (localX === 6 && localY === 2) object = 'ruin_arch_fragment';
      if (localX === 8 && localY === 3) object = 'ruin_column';
      if (localX === 7 && localY === 4) object = 'debris';
      return { object: roadDistance >= 3 ? object : null };
    }
  }

  if (terrain === 'canyon_floor' && seed < 0.11 && localX >= 7 && localX <= 9 && localY >= 6 && localY <= 8) {
    const object = localX === 8 && localY === 7 ? 'cactus' : null;
    return { object: roadDistance >= 3 ? object : null };
  }

  if (terrain === 'volcanic_floor' && seed < 0.12 && localX >= 7 && localX <= 9 && localY >= 6 && localY <= 8) {
    const object = localX === 8 && localY === 7 ? 'volcanic_basalt' : null;
    return { object: roadDistance >= 3 ? object : null };
  }

  if (terrain === 'astral_floor' && seed < 0.1 && localX >= 7 && localX <= 9 && localY >= 6 && localY <= 8) {
    const object = localX === 8 && localY === 7 ? 'astral_crystals' : null;
    return { object: roadDistance >= 3 ? object : null };
  }

  return null;
}

/**
 * Prop types for ground layer.
 * Uses zoning: plaza (sparse, server provides buildings), town edge (fences/barrels),
 * road buffer (clear), wild (clustered natural objects).
 */
const TOWN_EDGE_GROUND_OBJECTS: Record<string, readonly string[]> = {
  meadow: ['barrel', 'crate', 'cart', 'lantern', 'bench', 'hay_bale', 'wagon_wheel', 'planter'],
  canyon: ['barrel', 'crate', 'cart', 'lantern', 'bench', 'wagon_wheel', 'planter'],
  ruins: ['barrel', 'crate', 'cart', 'lantern', 'bench', 'wagon_wheel', 'planter'],
  swamp: ['barrel', 'crate', 'cart', 'lantern'],
};

export function proceduralObject(
  wx: number,
  wy: number,
  terrain: string,
  context?: WorldTerrainContext | null,
): string | null {
  // Inside plaza: no procedural props (server buildings handle this)
  if (terrain === 'plaza') {
    return null;
  }

  const ctx = context;
  if (ctx?.towns.some(t => t.authored && Math.hypot(wx-t.cx,wy-t.cy) <= t.radius+2)) return null;
  const rd = ctx ? roadClearance(wx, wy, ctx) : Number.POSITIVE_INFINITY;

  // Road buffer: keep clear for passage
  if (rd < 3.0) return null;

  const effectiveBiome = (ctx ? nearestTown(wx, wy, ctx.towns)?.biome ?? ctx.wildernessBiome : undefined)
    ?? ({ forest_floor: 'forest', grass: 'meadow', canyon_floor: 'canyon', ruins_floor: 'ruins', swamp_floor: 'swamp',
      volcanic_floor: 'volcanic', astral_floor: 'astral', winter_floor: 'winter', jungle_floor: 'jungle' }[terrain]);

  // Town edge zone: sparse village decor (no trees/ruins)
  if (ctx && ctx.towns.length > 0) {
    const edge = townEdgeDist(wx, wy, ctx.towns);
    if (edge >= -2.0 && edge < 5.0) {
      // Forest handmade props are authored scene/town objects, never ambient edge noise.
      if (effectiveBiome === 'forest' || effectiveBiome === 'jungle' || effectiveBiome === 'winter' || effectiveBiome === 'volcanic') return null;
      const h = tileHash(wx, wy, 301);
      const commonKind = h < 0.03 ? 'barrel' : h < 0.048 ? 'crate' : h < 0.066 ? 'cart' : h < 0.078 ? 'lantern'
        : h < 0.09 ? 'bench' : h < 0.1 ? 'hay_bale' : h < 0.108 ? 'wagon_wheel' : h < 0.116 ? 'planter' : null;
      if (commonKind) {
        return TOWN_EDGE_GROUND_OBJECTS[effectiveBiome ?? '']?.includes(commonKind) ? commonKind : null;
      }
      if (h < 0.132) {
        if (effectiveBiome === 'canyon') return 'cactus';
        if (effectiveBiome === 'swamp') return 'swamp_berry_bush';
        if (effectiveBiome === 'astral' || effectiveBiome === 'meadow' || effectiveBiome === 'ruins') return 'bush';
      }
      if (h < 0.158) {
        if (effectiveBiome === 'canyon') return 'rock';
        if (effectiveBiome === 'swamp') return 'marsh_reeds';
        if (effectiveBiome === 'astral') return 'astral_crystals';
        if (effectiveBiome === 'meadow') return 'leaves';
        if (effectiveBiome === 'ruins') return 'ruin_rubble';
      }
      return null;
    }
  }

  // Forest identity belongs to the biome, including its dirt/stone ground noise.
  // Authored modular scenes own all story props; ambient forest contains only vegetation.
  if (effectiveBiome === 'forest' || effectiveBiome === 'jungle') {
    const h = tileHash(wx, wy, 137);
    const ch = clusterHash(wx, wy, 137);
    const treeThreshold = ch < 0.45 ? 0.2 : 0.06;
    if (h < treeThreshold) return 'tree';
    const bushThreshold = ch < 0.35 ? 0.06 : 0.02;
    if (h < treeThreshold + bushThreshold) return 'bush';
    const sparse = tileHash(wx, wy, 199);
    if (sparse < 0.008) return 'stump';
    if (sparse < 0.016) return 'fallen_log';
    if (effectiveBiome === 'jungle' && sparse < 0.027) return 'meadow_wildflowers';
    return null;
  }

  const composition = compositionTile(wx, wy, terrain, rd, ctx);
  if (composition) return composition.object;

  // Open and rocky biomes use their own sparse props instead of forest groves.
  if (terrain === 'canyon_floor') {
    const patch = clusterHash(wx, wy, 743);
    if (patch < 0.12 && tileHash(wx, wy, 744) < 0.18) return 'rock';
    if (tileHash(wx, wy, 745) < 0.012) return 'cactus';
    // Occasional 3x3 groves break up empty rock fields without forest-level coverage.
    const treeGrove = clusterHash(wx, wy, 746);
    const treeThreshold = treeGrove < 0.24 ? 0.08 : 0.02;
    if (tileHash(wx, wy, 747) < treeThreshold) return 'tree';
    return null;
  }
  if (terrain === 'volcanic_floor') {
    const patch = clusterHash(wx, wy, 753);
    if (patch < 0.1 && tileHash(wx, wy, 754) < 0.12) return 'volcanic_basalt';
    const treePatch = clusterHash(wx, wy, 755);
    if (tileHash(wx, wy, 756) < (treePatch < 0.25 ? 0.08 : 0.025)) return 'tree';
    if (tileHash(wx, wy, 757) < 0.006) return 'volcanic_basalt';
    return null;
  }
  if (terrain === 'astral_floor') {
    if (tileHash(wx, wy, 764) < 0.08) return 'tree';
    if (tileHash(wx, wy, 763) < 0.035) return 'astral_crystals';
    return null;
  }

  // Fallback ambient generation stays within the same biome families as the server.
  const h = tileHash(wx, wy, 137);
  const ch = clusterHash(wx, wy, 137);
  const sparse = tileHash(wx, wy, 199);
  if (terrain === 'grass') {
    let treeThreshold = 0.07;
    if (ch < 0.15) treeThreshold = 0.09;
    if (h < treeThreshold) return 'tree';
    let bushThreshold = 0.007;
    if (ch < 0.2) bushThreshold = 0.012;
    if (h < treeThreshold + bushThreshold) return 'bush';
    if (sparse < 0.009) return 'meadow_wildflowers';
    if (sparse < 0.026) return 'leaves';
    return null;
  }
  if (terrain === 'swamp_floor') {
    let treeThreshold = 0.02;
    if (ch < 0.28) treeThreshold = 0.16;
    if (h < treeThreshold) return 'tree';
    if (h < treeThreshold + 0.03) return 'swamp_berry_bush';
    if (h < treeThreshold + 0.07) return 'marsh_reeds';
    return null;
  }
  if (terrain === 'ruins_floor') {
    if (h < 0.055) return 'ruin_rubble';
    if (h < 0.145) return 'tree';
    if (h < 0.205) return 'bush';
    if (sparse < 0.022) return 'ruin_rubble';
    return null;
  }
  if (terrain === 'winter_floor') {
    let treeThreshold = 0.02;
    if (ch < 0.3) treeThreshold = 0.08;
    if (h < treeThreshold) return 'tree';
    if (sparse < 0.12) return 'snow_drift';
    return null;
  }

  return null;
}

/** Blocking object types that hero cannot walk through */
const BLOCKING_TYPES = new Set(['tree', 'bush', 'rock', 'ruin', 'stall', 'well', 'barrel']);

/**
 * Check if a tile at the given world coordinate is blocked by a procedural obstacle.
 */
export function isProcedurallyBlocked(
  wx: number,
  wy: number,
  context?: WorldTerrainContext | null,
): boolean {
  const ix = Math.floor(wx);
  const iy = Math.floor(wy);
  const terrain = proceduralTerrain(ix, iy, context);
  const obj = proceduralObject(ix, iy, terrain, context);
  if (obj === null) return false;
  return BLOCKING_TYPES.has(obj);
}
